import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import ContentHeader from '../ContentHeader'
import { useClearUserInfo, useI18n } from 'contexts/AuthContext'
import { Button } from 'antd'
import { mcaScreenerPromise, updateUserPromise } from 'apis/FitOnCareAPI'
import handIcon from '../img/ic-hand.svg'
import { getFitOnHost, getWebFitOnHost } from 'apis/formatUrl'
import { useMCA } from './useMCA'
import { logAmplitudeEvent } from 'utils/amplitudeInit'
import { getAutoLoginParams } from 'utils/basic'
import { useDeeplink } from 'hooks/useDeeplink'
import { USE_NATIVE_MCA_SIGNUP } from 'config'
import { getUserEmail } from 'utils/authLocalStorage'

const AlreadyEnrolled = (props) => {
  const { i18n } = useI18n()
  const navigate = useNavigate()
  const { sku } = props
  const { getMcaEnrollment } = useMCA({ sku })
  const { fitonUserId: urlFitonUserId, email } = useDeeplink()
  const { clearAll } = useClearUserInfo()
  const [fetchedFitonUserId, setFetchedFitonUserId] = useState()
  const fitonUserId = urlFitonUserId || fetchedFitonUserId

  useEffect(() => {
    props.setRemoveEventListener(true)
  }, [])

  // The URL doesn't always carry `u` (fitonUserId) - e.g. reaching this
  // screen via Welcome.js's native auto-login resume rather than a deeplink
  // that sets it. onNext needs it to build the auto-login handoff back into
  // the FitOn host, so fetch it from the screener when it's missing.
  useEffect(() => {
    if (urlFitonUserId) return
    mcaScreenerPromise().then((resp) => {
      setFetchedFitonUserId(resp.data?.fitonUserId?.toString())
    })
  }, [urlFitonUserId])

  useEffect(() => {
    getMcaEnrollment().then((enrollment) => {
      logAmplitudeEvent('Screen View: Onboarding - Already Enrolled', {
        sku,
        existing_skus: enrollment?.existingCareSkus || [],
      })
    })
  }, [getMcaEnrollment, sku])

  const onNext = () => {
    logAmplitudeEvent('Click: Onboarding - Already Enrolled - Go To Care', {
      sku,
    })
    window.location.href = `${getFitOnHost()}?${getAutoLoginParams({ fitOnUserId: fitonUserId, fitOnEmail: email || getUserEmail() })}`
  }

  const onBack = (e) => {
    updateUserPromise({ activate: 0, skuStatus: { sku, status: 1 } })
    props.setRemoveEventListener(false)
    props.onBack(e)
  }

  // Mirrors Welcome.js's USE_NATIVE_MCA_SIGNUP backdoor (config.js): when
  // false, fall straight back to the original raw <a href> out to
  // fiton-web's /login?r=medicare-signup&switchAccount=1, untouched. When
  // true (default), clear the current FitOn/Care session
  // (useClearUserInfo().clearAll() — same as switchAccount=1's intent of
  // bypassing the "already logged in" auto-redirect) and route to the
  // native /login screen so a different account can sign in.
  const onSwitchAccount = () => {
    if (!USE_NATIVE_MCA_SIGNUP) {
      window.location.href =
        getWebFitOnHost() + '/login?r=medicare-signup&switchAccount=1'
      return
    }
    clearAll()
    // This screen only exists in the MCA flow, so the native /login route
    // needs variant: 'mca' in state — same as Welcome.js's
    // redirectToMedicareSignup — to gate the MCA-specific analytics events
    // and resume the right SKU on the post-login auto-login handoff.
    navigate('/login', { state: { variant: 'mca' } })
  }

  return (
    <div className='info-sec'>
      <ContentHeader {...props} onBack={onBack} />
      <div className='info-content'>
        <img src={handIcon} alt='' className='top-icon' />
        <div className='title-pns'>
          {i18n('You are already enrolled in our Care programs')}
        </div>
        <div
          className='subtitle'
          style={{
            marginBottom: '10px',
            marginTop: '17px',
            marginInline: '0',
          }}
        >
          {i18n(
            'You can continue in app to access your programs and benefits.'
          )}
        </div>
        <Button
          className='btn-primary small-btn'
          style={{ width: '100%', marginBottom: '24px' }}
          onClick={onNext}
        >
          {i18n('Back to App')}
        </Button>

        <div className='center-align'>
          {i18n('Not you?')}{' '}
          <span
            onClick={onSwitchAccount}
            className='clickable hover-link gradient-font-2 underline-link'
          >
            {i18n('Log in with another account')}
          </span>
        </div>
      </div>
    </div>
  )
}

export default AlreadyEnrolled
